"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = PasswordInput;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = __importDefault(require("react"));
const react_native_1 = require("react-native");
const react_native_paper_1 = require("react-native-paper");
const react_native_fontawesome_1 = require("@fortawesome/react-native-fontawesome");
const Theme_1 = require("../Theme");
const ThemeContext_1 = require("../ThemeContext");
function useCapsLockWarning(enabled) {
    const [capsOn, setCapsOn] = react_1.default.useState(false);
    react_1.default.useEffect(() => {
        if (react_native_1.Platform.OS !== "web" || !enabled)
            return;
        const w = globalThis;
        if (!w?.addEventListener)
            return;
        const handler = (e) => setCapsOn(!!e?.getModifierState?.("CapsLock"));
        w.addEventListener("keydown", handler, true);
        w.addEventListener("keyup", handler, true);
        return () => {
            w.removeEventListener("keydown", handler, true);
            w.removeEventListener("keyup", handler, true);
        };
    }, [enabled]);
    return capsOn;
}
function mergeRefs(...refs) {
    return (value) => {
        refs.forEach((ref) => {
            if (!ref)
                return;
            if (typeof ref === "function")
                ref(value);
            else
                ref.current = value;
        });
    };
}
function PasswordInput(props) {
    const THEME = (0, ThemeContext_1.useTheme)();
    const paperTheme = (0, react_native_paper_1.useTheme)();
    const [visible, setVisible] = react_1.default.useState(false);
    const isWeb = react_native_1.Platform.OS === "web";
    const capsEnabled = isWeb && (props.showCapsWarning ?? true);
    const capsOn = useCapsLockWarning(capsEnabled);
    const showCapsInline = capsEnabled && capsOn;
    const selectionRef = react_1.default.useRef({ start: 0, end: 0 });
    const pendingSelectionRef = react_1.default.useRef(null);
    const ignoreSelectionRef = react_1.default.useRef(false);
    const paperInnerRef = react_1.default.useRef(null);
    const flattenedStyle = react_native_1.StyleSheet.flatten(props.style) || {};
    const baseTextColor = flattenedStyle?.color ??
        paperTheme.colors.onSurface ??
        paperTheme.colors?.text ??
        THEME.Text;
    const iconColor = props.disabled
        ? (paperTheme.colors.onSurfaceDisabled ?? THEME.TextDisabled)
        : baseTextColor;
    const RIGHT_INSET = 12;
    const ICON_GAP = 8;
    const EYE_TOUCH = 16;
    const CAPS_ICON_W = 20;
    const RIGHT_SLOT_W = capsEnabled ? (CAPS_ICON_W + ICON_GAP + EYE_TOUCH) : EYE_TOUCH;
    const INPUT_RIGHT_PADDING = RIGHT_INSET + RIGHT_SLOT_W + 8;
    const EyeIcon = ((0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: visible ? Theme_1.ICONS.Regular.EyeSlash : Theme_1.ICONS.Regular.Eye, color: iconColor, size: 16 }));
    const CapsIcon = ((0, jsx_runtime_1.jsx)(react_native_fontawesome_1.FontAwesomeIcon, { icon: Theme_1.ICONS.Solid.SquareA, color: iconColor, size: 20 }));
    const onToggle = () => {
        if (isWeb) {
            pendingSelectionRef.current = selectionRef.current;
            ignoreSelectionRef.current = true;
        }
        setVisible((v) => !v);
    };
    react_1.default.useEffect(() => {
        if (!isWeb)
            return;
        const sel = pendingSelectionRef.current;
        if (!sel)
            return;
        const input = props.kind === "paper"
            ? props.inputRef?.current ?? paperInnerRef.current
            : props.inputRef?.current;
        setTimeout(() => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    input?.focus?.();
                    if (typeof input?.setNativeProps === "function") {
                        input.setNativeProps({ selection: sel });
                    }
                    else if (typeof input?.setSelectionRange === "function") {
                        input.setSelectionRange(sel.start, sel.end);
                    }
                    pendingSelectionRef.current = null;
                    ignoreSelectionRef.current = false;
                });
            });
        }, 0);
    }, [visible, isWeb, props.kind]);
    const RightOverlay = ((0, jsx_runtime_1.jsxs)(react_native_1.View, { pointerEvents: "box-none", style: {
            position: "absolute",
            right: RIGHT_INSET,
            top: 0,
            bottom: 0,
            width: RIGHT_SLOT_W,
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
        }, children: [capsEnabled && ((0, jsx_runtime_1.jsx)(react_native_1.View, { pointerEvents: "none", style: { opacity: showCapsInline ? 1 : 0, marginRight: ICON_GAP }, children: CapsIcon })), (0, jsx_runtime_1.jsx)(react_native_1.Pressable, { onMouseDown: (e) => isWeb && e.preventDefault(), onPointerDown: (e) => isWeb && e.preventDefault(), onPress: onToggle, hitSlop: { top: 10, bottom: 10, left: 0, right: 10 }, ...(isWeb ? { tabIndex: -1 } : null), style: { width: EYE_TOUCH, height: EYE_TOUCH, alignItems: "center", justifyContent: "center" }, children: EyeIcon })] }));
    if (props.kind === "native") {
        return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { gap: 6 }, children: (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: { position: "relative" }, children: [(0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ref: props.inputRef, placeholder: props.placeholder, placeholderTextColor: props.placeholderTextColor, "aria-label": props.ariaLabel, secureTextEntry: !visible, value: props.value, onChangeText: props.onChangeText, onSubmitEditing: props.onSubmitEditing, onFocus: props.onFocus, onBlur: props.onBlur, onSelectionChange: (e) => {
                            if (ignoreSelectionRef.current)
                                return;
                            selectionRef.current = e.nativeEvent.selection;
                        }, style: [props.style, { paddingRight: INPUT_RIGHT_PADDING }] }), RightOverlay] }) }));
    }
    const { kind, showCapsWarning, inputRef, ...paperProps } = props;
    const finalRef = inputRef ?? paperInnerRef;
    return ((0, jsx_runtime_1.jsx)(react_native_1.View, { style: { gap: 6 }, children: (0, jsx_runtime_1.jsx)(react_native_paper_1.TextInput, { ...paperProps, secureTextEntry: !visible, right: undefined, render: (renderProps) => {
                const rp = react_native_1.StyleSheet.flatten(renderProps.style) || {};
                const wrapperStyle = {
                    position: "relative",
                    height: rp.height,
                    minHeight: rp.minHeight,
                    flex: rp.flex,
                };
                return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: wrapperStyle, children: [(0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ...renderProps, ref: mergeRefs(renderProps.ref, finalRef), style: [renderProps.style, { paddingRight: INPUT_RIGHT_PADDING }], onSelectionChange: (e) => {
                                if (ignoreSelectionRef.current)
                                    return;
                                selectionRef.current = e.nativeEvent.selection;
                                renderProps.onSelectionChange?.(e);
                            } }), RightOverlay] }));
            } }) }));
}
